import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// FIX: Import missing types for the processing queue feature.
import { apiService, QueueItem, QueueLog, ProcessorStatus, QueueSummary } from '../services/apiService';
import { Spinner } from './Spinner';
import { PlayIcon } from './icons/PlayIcon';
import { PauseIcon } from './icons/PauseIcon';
import { DeleteIcon } from './icons/DeleteIcon';
import { ConfirmModal } from './ConfirmModal';
import { logger } from '../services/logger';
import { Pagination } from './Pagination';
import { PROCESSING_PAGE_SIZE_PREF_KEY } from '../types';

const statusConfig: Record<QueueItem['status'], { text: string; color: string }> = {
    pending: { text: 'Čeká', color: 'bg-yellow-100 text-yellow-800' },
    processing: { text: 'Zpracovává se', color: 'bg-blue-100 text-blue-800' },
    completed: { text: 'Dokončeno', color: 'bg-green-100 text-green-800' },
    failed: { text: 'Selhalo', color: 'bg-red-100 text-red-800' },
};

const logLevelColor: Record<QueueLog['level'], string> = {
    INFO: 'text-gray-700',
    DEBUG: 'text-blue-700',
    WARN: 'text-orange-700',
    ERROR: 'text-red-700',
};

const formatLogTimestamp = (isoString: string): string => {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};


export const ProcessingView: React.FC = () => {
    const [queue, setQueue] = useState<QueueItem[]>([]);
    const [logs, setLogs] = useState<QueueLog[]>([]);
    const [logPage, setLogPage] = useState(1);
    const [totalLogPages, setTotalLogPages] = useState(1);
    const [processorStatus, setProcessorStatus] = useState<ProcessorStatus>('idle');
    const [isLoading, setIsLoading] = useState(true);
    const [itemToDelete, setItemToDelete] = useState<QueueItem | null>(null);
    
    const [filter, setFilter] = useState('');
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
    
    const lastKnownState = useRef('');

    useEffect(() => {
        const loadPageSize = async () => {
            const savedSize = await apiService.getPreference<number>(PROCESSING_PAGE_SIZE_PREF_KEY, 20);
            setPageSize(savedSize);
        };
        loadPageSize();
    }, []);
    
    useEffect(() => {
        let isMounted = true;
    
        const fetchData = async (isInitialCall: boolean) => {
            if (!isMounted) return;
    
            if (isInitialCall) {
                setIsLoading(true);
            }
    
            try {
                const summary = await apiService.getQueueSummary();
                if (!isMounted) return;
    
                setProcessorStatus(summary.status);
                
                // Vytvoříme reprezentaci stavu pro porovnání. Zahrneme i počty, abychom detekovali změny.
                const currentState = `${summary.status}-${summary.lastUpdate || ''}-${JSON.stringify(summary.counts)}`;
    
                if (isInitialCall || currentState !== lastKnownState.current) {
                    lastKnownState.current = currentState;
                    logger.debug(`ProcessingView: ${isInitialCall ? 'Initial load' : 'State change detected'}, fetching full queue and logs.`);
                    
                    const [queueData, logData] = await Promise.all([
                        apiService.getQueue(),
                        apiService.getQueueLogs(logPage)
                    ]);
    
                    if (isMounted) {
                        setQueue(queueData);
                        setLogs(logData.logs);
                        setTotalLogPages(logData.totalPages);
                    }
                }
            } catch (error) {
                logger.error('Failed to poll summary and data', error);
            } finally {
                if (isMounted && isInitialCall) {
                    setIsLoading(false);
                }
            }
        };
        
        fetchData(true);
        const interval = setInterval(() => fetchData(false), 3000);
        
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [logPage]);


    useEffect(() => {
        setCurrentPage(1);
    }, [filter, pageSize]);

    const filteredQueue = useMemo(() => {
        if (!filter) return queue;
        const lowercasedFilter = filter.toLowerCase();
        return queue.filter(item =>
            item.originalFilename.toLowerCase().includes(lowercasedFilter) ||
            statusConfig[item.status].text.toLowerCase().includes(lowercasedFilter) ||
            (item.errorMessage && item.errorMessage.toLowerCase().includes(lowercasedFilter))
        );
    }, [queue, filter]);

    const totalPages = useMemo(() => {
        if (pageSize === 0 || filteredQueue.length === 0) return 1;
        return Math.ceil(filteredQueue.length / pageSize);
    }, [filteredQueue.length, pageSize]);

    const paginatedQueue = useMemo(() => {
        if (pageSize === 0) return filteredQueue;
        const startIndex = (currentPage - 1) * pageSize;
        return filteredQueue.slice(startIndex, startIndex + pageSize);
    }, [filteredQueue, currentPage, pageSize]);
    
    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        apiService.setPreference(PROCESSING_PAGE_SIZE_PREF_KEY, newSize);
    };

    const handleStart = async () => await apiService.startQueue();
    const handleStop = async () => await apiService.stopQueue();
    const handleClear = () => setIsClearConfirmOpen(true);
    const handleDelete = (item: QueueItem) => setItemToDelete(item);
    
    const handleConfirmClear = async () => {
        await apiService.clearCompletedInQueue();
        setIsClearConfirmOpen(false);
        // Data will be refetched by the polling mechanism
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete) {
            await apiService.deleteQueueItem(itemToDelete.id);
            setItemToDelete(null);
            // Data will be refetched by the polling mechanism
        }
    };

    return (
        <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
                    <h3 className="text-xl font-semibold text-gray-700">Fronta zpracování</h3>
                    <div className="flex items-center gap-2 flex-wrap">
                        {processorStatus === 'idle' || processorStatus === 'stopping' ? (
                            <button onClick={handleStart} disabled={processorStatus === 'stopping'} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400">
                                <PlayIcon className="w-5 h-5" /> Spustit
                            </button>
                        ) : (
                            <button onClick={handleStop} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600">
                                <PauseIcon className="w-5 h-5" /> Zastavit
                            </button>
                        )}
                        <button onClick={handleClear} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                           Vyčistit dokončené
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-4 mb-4">
                    <input
                        type="text"
                        placeholder={`Filtrovat z ${queue.length} položek...`}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="flex-grow w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <label htmlFor="page-size-select-queue" className="text-sm text-gray-600 whitespace-nowrap">Na stránku:</label>
                        <select
                            id="page-size-select-queue"
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            className="block w-auto pl-3 pr-8 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="40">40</option>
                            <option value="60">60</option>
                            <option value="0">Vše</option>
                        </select>
                    </div>
                </div>


                {isLoading ? <Spinner text="Načítám stav fronty..." /> : (
                <>
                    <div className="overflow-x-auto border rounded-md">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Soubor</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stav</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Přidáno</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Akce</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedQueue.map(item => (
                                    <tr key={item.id}>
                                        <td className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap" title={item.originalFilename}>{item.originalFilename}</td>
                                        <td className="px-4 py-2 text-sm whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusConfig[item.status]?.color}`}>
                                                {statusConfig[item.status]?.text}
                                            </span>
                                            {item.status === 'failed' && <p className="text-xs text-red-600 mt-1" title={item.errorMessage || ''}>{item.errorMessage}</p>}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{new Date(item.createdAt).toLocaleString('cs-CZ')}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                                            {(item.status === 'pending' || item.status === 'failed') && (
                                                <button onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-800" title="Odstranit z fronty">
                                                    <DeleteIcon className="w-5 h-5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {queue.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-center text-gray-500 py-10">Fronta je prázdná.</td>
                                    </tr>
                                )}
                                {queue.length > 0 && paginatedQueue.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-center text-gray-500 py-10">Pro zadaný filtr nebyly nalezeny žádné položky.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </>
                )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                 <h3 className="text-xl font-semibold text-gray-700 mb-4">Logy zpracování</h3>
                 <div className="border rounded-md font-mono text-xs overflow-hidden">
                    <div className="bg-gray-100 p-2 h-72 overflow-y-auto">
                        {logs.map(log => (
                            <p key={log.id} className={`${logLevelColor[log.level]} whitespace-pre-wrap`}>
                                <span className="text-gray-400 whitespace-nowrap">{formatLogTimestamp(log.timestamp)}</span>
                                <span className="font-bold mx-2">[{log.level}]</span>
                                <span>{log.message}</span>
                            </p>
                        ))}
                         {logs.length === 0 && !isLoading && (
                            <p className="text-center text-gray-500 py-10">Žádné logy k zobrazení.</p>
                        )}
                    </div>
                     <div className="flex justify-between items-center p-2 bg-gray-50 border-t">
                        <button onClick={() => setLogPage(p => Math.max(1, p - 1))} disabled={logPage === 1} className="px-3 py-1 text-sm rounded-md bg-white border hover:bg-gray-100 disabled:opacity-50">
                           Novější
                        </button>
                        <span className="text-sm text-gray-600">Stránka {logPage} z {totalLogPages}</span>
                        <button onClick={() => setLogPage(p => Math.min(totalLogPages, p + 1))} disabled={logPage >= totalLogPages} className="px-3 py-1 text-sm rounded-md bg-white border hover:bg-gray-100 disabled:opacity-50">
                           Starší
                        </button>
                    </div>
                 </div>
            </div>

            <ConfirmModal
                isOpen={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Odstranit položku z fronty?"
                confirmText="Odstranit"
                confirmClassName="bg-red-600 text-white rounded-md hover:bg-red-700"
            >
                {itemToDelete && <p>Opravdu chcete trvale odstranit soubor <strong>{itemToDelete.originalFilename}</strong> z fronty? Tato akce je nevratná.</p>}
            </ConfirmModal>

            <ConfirmModal
                isOpen={isClearConfirmOpen}
                onClose={() => setIsClearConfirmOpen(false)}
                onConfirm={handleConfirmClear}
                title="Vyčistit dokončené položky?"
                confirmText="Vyčistit"
                confirmClassName="bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
                <p>Opravdu chcete odstranit všechny dokončené a selhané položky z fronty? Tato akce je nevratná.</p>
            </ConfirmModal>
        </div>
    );
};
